import {
    Badge,
    CheckIn,
    GetPointResponse,
    GetTopicResponse,
    Location,
    NumberedIconShape,
    ObjectId,
    TopLevelMarkerHolder,
    TopLevelShapeHolder,
    TopicCategory,
} from '../types';
import { DEFAULT_CATEGORY_COLORS, DEFAULT_CATEGORY_ICON, DEFAULT_ICON_SIZE } from './constants';
import { addListenerToShape, addShapeToMap, createShapeHolderFromCustomShape, hideShape, showShape } from './shapes';
import { getColorFromCircleIcon, getFeaturedSvgAsDataUrl, isGeneratedColorIcon, makeColorIntoDataSvg, makeColorsIntoNumberSvg } from '../helpers/colors';
import { getMarkerElement, getMarkerPosition, isDefined, makeId, setMarkerDraggable, setMarkerMap, updateMarkerIcon, updateMarkerPosition, updateMarkerVisibility, updateMarkerZIndex } from '../helpers/utils';
import { getValidatedIcon, preloadIcons } from './icon-loader';
import { getCheckinKey } from './api/checkin-api';
import pointCache, { MarkerCallbackFunction } from './state/point-cache';

import { Marker } from '@twitchaxorg/markerclusterer';
import { MarkerService } from './api/google-api';
import challengeData from './state/challenge-data';
import { palette } from '../style/variables';
import topicData from './state/topic-data';

const hiddenMarkerIcon = '/images/hidden_point_marker.png';
const checkedInMarkerIcon = '/images/checked_in_big.png';
const oldPointIcon = 'https://img.icons8.com/?size=48&id=hRIvjOSQ8I0i&format=png';

// Preload critical icons
getValidatedIcon(oldPointIcon);

/**
 * Preload all custom and category icons from a list of points in parallel
 * Call this before instantiating markers for fast synchronous icon access
 */
export async function preloadCustomIcons(points: GetPointResponse[], categories: TopicCategory[]): Promise<void> {
    const customIconUrls = points.filter(p => p.custom_icon_url !== undefined && p.custom_icon_url !== null)
        .map(p => p.custom_icon_url) as string[];

    const categoriesIcons = categories
        .map(c => c.icon)
        .filter(icon => icon !== undefined && icon !== null);

    // Remove duplicates
    const uniqueUrls = Array.from(new Set(customIconUrls.concat(categoriesIcons)));

    if (uniqueUrls.length === 0) return;

    // Preload all in parallel
    await preloadIcons(uniqueUrls);
}

export function getMarkerIcon(
    point: GetPointResponse,
    checkins: Map<String, CheckIn[]>,
    saved_points: String[],
    isFocused: boolean,
    isHidden = false
): google.maps.Icon {

    const category = topicData.categories.find(
        (c) => c._id?.$oid === point.topic_category_id?.$oid
    );

    const iconSize = category?.icon_size ?? topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE;
    if (!point.active) {
        return {
            url: '/images/ic_inactive.svg',
            scaledSize: new google.maps.Size(iconSize, iconSize),
            anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
        };
    }

    if (isFocused) {
        return {
            url: '/images/selected_marker.png',
            scaledSize: new google.maps.Size(48, 48),
            anchor: new google.maps.Point(24, 24),
        };
    }

    if (isHidden) {
        return {
            url: hiddenMarkerIcon,
            scaledSize: new google.maps.Size(iconSize, iconSize),
            anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
        };
    }

    if (point.is_out_of_date) {
        return {
            url: oldPointIcon,
            scaledSize: new google.maps.Size(iconSize * .75, iconSize * .75),
            anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
        };
    }

    /* Replace icons with a new icon for locations with checkins */
    if (checkins.has(getCheckinKey(point.location)) || saved_points.includes(point._id.$oid)) {
        return {
            url: checkedInMarkerIcon,
            scaledSize: new google.maps.Size(iconSize, iconSize),
            anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
        };
    }

    if (
        topicData.topic?.enable_point_numbering === true &&
        topicData.topic?.replace_icons_with_numbers === true &&
        point.number !== undefined
    ) {
        return {
            url: makeColorsIntoNumberSvg(
                getNumberedPointIconColor(topicData.topic, point.topic_category_id.$oid),
                point.number,
                getNumberedPointIconShape(topicData.topic, point.topic_category_id.$oid)
            ),
            scaledSize: new google.maps.Size(iconSize, iconSize),
            anchor: new google.maps.Point(iconSize / 2, iconSize / 2),
        };
    }

    // Use validated icon with fallback for custom icons.
    // If preloadIcons() has already cached this URL, getValidatedIcon() will return a synchronously validated URL.
    // If preloadIcons() has not been called, getValidatedIcon() may return the original URL and the icon could still fail to load.
    // Callers that rely on guaranteed validation should ensure preloadIcons() has run before invoking getMarkerIcon().
    const iconUrl = point.custom_icon_url
        ? getValidatedIcon(point.custom_icon_url, getCategoryIcon(category?._id) ?? DEFAULT_CATEGORY_ICON)
        : getCategoryIcon(category?._id) ?? DEFAULT_CATEGORY_ICON;

    return {
        url: iconUrl,
        scaledSize: new google.maps.Size(iconSize, iconSize),
        anchor: new google.maps.Point(iconSize / 2, iconSize / 2)
    };
}

export function getPreviewIcon(topic: GetTopicResponse | undefined, custom_icon_url: string | undefined, point_number: string | undefined, topic_category_id: string | undefined): string {
    if (topic === undefined || topic_category_id === undefined) {
        return '';
    }

    const category = topic.categories.find(
        (c) => c._id?.$oid === topic_category_id
    );

    const numberedPointIconColor = getNumberedPointIconColor(topic, topic_category_id);

    if (topic.enable_point_numbering === true && topic.replace_icons_with_numbers === true && point_number !== undefined) {
        return makeColorsIntoNumberSvg(
            numberedPointIconColor ?? '#102C3D',
            point_number,
            getNumberedPointIconShape(topic, topic_category_id)
        );
    }

    return custom_icon_url ?? getCategoryIcon(category?._id) ?? DEFAULT_CATEGORY_ICON;
}

export function getNumberedPointIconColor(topic: GetTopicResponse | undefined, topic_category_id: string | undefined): string | undefined {
    if (topic === undefined || topic_category_id === undefined) {
        return;
    }

    const category = topic.categories.find(
        (c) => c._id?.$oid === topic_category_id
    );

    return category?.icon?.startsWith('data:image/svg+xml') === true ? getColorFromCircleIcon(category.icon) : topic.point_number_circle_color;
}

export function getCategoryMarkerShape(topic: GetTopicResponse | undefined, topic_category_id: string | undefined): NumberedIconShape {
    if (topic === undefined || topic_category_id === undefined) {
        return NumberedIconShape.Circle;
    }

    const category = topic.categories.find(
        (c) => c._id?.$oid === topic_category_id
    ) ;

    return category?.marker_shape ?? topic.marker_shape ?? NumberedIconShape.Circle;
}

export function getNumberedPointIconShape(topic: GetTopicResponse | undefined, topic_category_id: string | undefined): NumberedIconShape {
    return getCategoryMarkerShape(topic, topic_category_id);
}

export function getMarkerZindex(point: GetPointResponse, isFocused: boolean) {
    if (point.featured) {
        return 3;
    }
    if (isFocused) {
        return 4;
    }

    if (point.is_gate_hidden) {
        return 1;
    }

    return 2;
}

export function revertIconForMarker(
    markerHolder: TopLevelMarkerHolder | undefined,
) {
    if (markerHolder === undefined || topicData.topic === undefined || markerHolder?.point?.topic_category_id === undefined) {
        return;
    }

    const isHidden = markerHolder.point.is_gate_hidden && topicData.topic.is_gate_limited;
    const icon = getMarkerIcon(markerHolder.point, pointCache.checkins, challengeData?.savedLocationIds ?? [],false, isHidden);

    if (markerHolder.point.custom_shape) {
        showShape(markerHolder.customShape?.inner);
        if (icon.url !== checkedInMarkerIcon) {
            icon.scaledSize = new google.maps.Size(0, 0);
        }
    }
    // keep after the custom shape making icon 0,0
    updateMarkerIcon(markerHolder.inner, icon);

    updateMarkerZIndex(markerHolder.inner, markerHolder.zIndex ?? 1);

    // remove background circle
    if (markerHolder.inner instanceof google.maps.Marker) {
        markerHolder.focusDivs?.mBackgroundOverlay?.hide();
        markerHolder.focusDivs?.mTextBelowOverlay?.hide();
    } else {
        if (isDefined(markerHolder.focusDivs) && isDefined(markerHolder.focusDivs.aMBackgroundCircle)) {
            markerHolder.focusDivs.aMBackgroundCircle.style.display = 'none';
        }
        if (isDefined(markerHolder.focusDivs) && isDefined(markerHolder.focusDivs.aMTextBelow)) {
            markerHolder.focusDivs.aMTextBelow.style.display = 'none';
        }
    }

    if (markerHolder.halo) {
        updateMarkerVisibility(markerHolder.halo.inner, true);
    }

    pointCache.markerClusterer?.addMarker(markerHolder.inner);
}

export function setFocusIconForMarker(markerHolder: TopLevelMarkerHolder | undefined) {
    if (markerHolder === undefined || isDefined(markerHolder.customShape)) {
        return;
    }

    pointCache.markerClusterer?.removeMarker(markerHolder.inner);

    // show marker
    updateMarkerVisibility(markerHolder.inner, true);
    if (pointCache.map) {
        setMarkerMap(markerHolder.inner, pointCache.map);
    }

    updateMarkerZIndex(markerHolder.inner, 1000);
    // force a mouseover event to trigger the hover effect
    if (markerHolder.inner instanceof google.maps.Marker) {
        createAndShowCustomOverlay(markerHolder);
    } else {
        if (isDefined(markerHolder.focusDivs) && isDefined(markerHolder.focusDivs.aMBackgroundCircle)) {
            markerHolder.focusDivs.aMBackgroundCircle.style.display = 'block';
        }
    }

}

export function setHiddenIconForMarker(markerHolder: TopLevelMarkerHolder | undefined) {
    if (markerHolder === undefined) {
        return;
    }

    const icon: google.maps.Icon = {
        url: hiddenMarkerIcon,
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
    };
    updateMarkerIcon(markerHolder.inner, icon);
    updateMarkerZIndex(markerHolder.inner, 0);

    if (markerHolder.halo) {
        updateMarkerVisibility(markerHolder.halo.inner, false);
    }

    // set overlay text to 'Hidden'
    const isHidden = markerHolder.point.is_gate_hidden;
    if (isDefined(markerHolder.focusDivs?.mTextBelowOverlay)) {
        try {
            // [ARoney] This is guaranteed to fail.
            markerHolder.focusDivs?.mTextBelowOverlay?.setContent(isHidden ? 'Hidden' : markerHolder.point.name ?? '');
        } catch (e) {
            console.error('Error setting content on overlay', e);
        }
    } else if (markerHolder.focusDivs?.aMTextBelow) {
        markerHolder.focusDivs.aMTextBelow.textContent = isHidden ? 'Hidden' : markerHolder.point.name ?? '';
    }
}

export function setRouteIconForMarker(markerHolder: TopLevelMarkerHolder | undefined, letterString: string) {
    if (markerHolder === undefined) {
        return;
    }

    const icon_letter =  makeColorsIntoNumberSvg(
            palette.successGreen,
            letterString
        );


    const icon: google.maps.Icon = {
        url: icon_letter,
        scaledSize: new google.maps.Size(50, 50),
        anchor: new google.maps.Point(25, 25),
    };
    updateMarkerIcon(markerHolder.inner, icon);
    updateMarkerZIndex(markerHolder.inner, 50);

    if (markerHolder.halo) {
        updateMarkerVisibility(markerHolder.halo.inner, false);
    }
}


export function addPointShapeToMap(marker: Marker) {
    const markerHolder = pointCache.allMarkers.find(m => m.inner === marker);
    if (!markerHolder?.customShape) {
        return;
    }
    showShape(markerHolder.customShape.inner);
}

export function removePointShapeFromMap(marker: Marker) {
    const markerHolder = pointCache.allMarkers.find(m => m.inner === marker);
    if (markerHolder?.customShape?.inner) {
        hideShape(markerHolder.customShape.inner);
    }
}

function createHaloMarker(
    point: GetPointResponse,
    map: google.maps.Map,
): TopLevelMarkerHolder | undefined {
    const category = topicData.categories.find(
        (c) => c._id?.$oid === point.topic_category_id?.$oid
    );

    const iconSize = category?.icon_size ?? topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE;
    const scaledSize = iconSize * 3;

    const icon: google.maps.Icon = {
        url: getFeaturedSvgAsDataUrl(topicData.topic?.featured_color),
        scaledSize: new google.maps.Size(scaledSize, scaledSize),
        anchor: new google.maps.Point(scaledSize / 2, scaledSize / 2),
    };

    if (point.featured) {
        // keep halo even for hidden points or else there are issues with the clusterer
        const haloMarker = getMarkerElement(map, point.location.latLng, icon, 1, pointCache.advancedMarkers);

        return {
            id: makeId(),
            inner: haloMarker,
            point,
        };
    }
}

export function addBadgeToMarker(topLevelMarkerHolder: TopLevelMarkerHolder, badge: Badge) {
    const existing_badges = topLevelMarkerHolder.badges ?? [];
    const new_badges = [...existing_badges, badge];
    topLevelMarkerHolder.badges = new_badges;
    if (!topicData.topic) {
        return;
    }
    // add winner icon to marker
    updateMarkerIcon(topLevelMarkerHolder.inner, {
        url: badge.icon,
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
    });
    pointCache.markerClusterer?.removeMarker(topLevelMarkerHolder.inner);
    pointCache.markerClusterer?.addMarker(topLevelMarkerHolder.inner);
    topLevelMarkerHolder.iconUrl = badge.icon;
}

export function removeBadgeFromMarker(topLevelMarkerHolder: TopLevelMarkerHolder, badge_name: string) {
    const existing_badges = topLevelMarkerHolder.badges ?? [];
    const new_badges = existing_badges.filter(badge => badge.name !== badge_name);
    topLevelMarkerHolder.badges = new_badges;
}

export function createMarkerHolder(
    point: GetPointResponse,
    map: google.maps.Map,
    point_key: string,
    checkins: Map<String, CheckIn[]>,
    markerClickCallback: MarkerCallbackFunction,
    markerDragEndCallback: MarkerCallbackFunction,
    isOptimized: boolean,
    isFocused: boolean,
): TopLevelMarkerHolder {
    const isHidden = point.is_gate_hidden && topicData.topic?.is_gate_limited === true;
    const zIndex = getMarkerZindex(point, isFocused);
    const category = topicData.topic?.categories.find(
        (c) => c._id?.$oid === point.topic_category_id?.$oid
    );
    const iconSize = category?.icon_size ?? topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE;
    const icon = getMarkerIcon(point, checkins, challengeData?.savedLocationIds, isFocused, isHidden);

    // Throw error if AdvancedMarkerElement is not available but required
    if (pointCache.advancedMarkers === true && (!MarkerService.AdvancedMarkerElement)) {
        throw new Error('MarkerService.AdvancedMarkerElement is undefined or null');
    }

    // Handle custom shapes if applicable
    let customShape: TopLevelShapeHolder | undefined;
    if (point.custom_shape) {
        if (icon.url !== checkedInMarkerIcon) {
            icon.scaledSize = new google.maps.Size(0, 0);
        }
        customShape = createShapeHolderFromCustomShape(point.custom_shape);
        addShapeToMap(customShape.inner, map);
        showShape(customShape.inner);
        addListenerToShape(customShape.inner, 'click', async () => await markerClickCallback(markerHolder));
    }

    const marker = getMarkerElement(map, point.location.latLng, icon, zIndex, pointCache.advancedMarkers, undefined, point.name ?? '');
    const maybeHaloMarker = point.featured ? createHaloMarker(point, map) : undefined;

    const markerHolder: TopLevelMarkerHolder = {
        id: makeId(),
        inner: marker,
        halo: maybeHaloMarker,
        point,
        iconUrl: icon.url,
        zIndex,
        customShape,
        map,
    };

    // Add event listeners for clicks, dragging, and drag end
    marker.addListener('click', async () => await markerClickCallback(markerHolder));
    marker.addListener('drag', () => handleMarkerDrag(markerHolder));
    marker.addListener('dragend', async () => await handleMarkerDragEnd(markerHolder, markerDragEndCallback, point));

    // Determine overlay styling based on marker type (advanced vs regular)
    // anything changed here should also be changed in the ELSE block below
    if (marker instanceof google.maps.Marker) {

        marker.addListener('mouseover', () => {
            updateMarkerZIndex(marker, 1000);

            createAndShowCustomOverlay(markerHolder);
        });

        // Add a mouseout to hide the overlays (`OverlayView` dynamically creates / destroys).

        marker.addListener('mouseout', () => {
            updateMarkerZIndex(marker, zIndex);

            if (isDefined(markerHolder.focusDivs?.mTextBelowOverlay)) {
                markerHolder.focusDivs?.mTextBelowOverlay?.hide();
            }
            if (isDefined(markerHolder.focusDivs?.mBackgroundOverlay)) {
                markerHolder.focusDivs?.mBackgroundOverlay?.hide();
            }
        });
    } else {
        const parentElement = marker.content?.parentElement;
        if (isDefined(parentElement)) {
            const backgroundDiv = createMarkerBackgroundDiv(true, iconSize);
            backgroundDiv.style.display = 'none';
            parentElement.appendChild(backgroundDiv);
            let textDiv: HTMLDivElement | undefined;
            if (isDefined(markerHolder.point.name)) {
                textDiv = createMarkerTextDiv(isHidden? 'Hidden' : markerHolder.point.name);
                textDiv.style.display = 'none';
                parentElement.appendChild(textDiv);
            }

            markerHolder.focusDivs = {
                aMBackgroundCircle: backgroundDiv,
                aMTextBelow: textDiv,
            };

            // add listers that make divs appear on hover
            marker.content?.addEventListener('mouseenter', () => {
                backgroundDiv.style.display = 'block';
                if (textDiv) {
                    textDiv.style.display = 'block';
                };
                updateMarkerZIndex(marker, 1000);
            });
            marker.content?.addEventListener('mouseleave', () => {
                backgroundDiv.style.display = 'none';
                if (textDiv) {
                    textDiv.style.display = 'none';
                };
                updateMarkerZIndex(marker, zIndex);});
        }
    }

    return markerHolder;
}

/**
 * Helper function to handle marker dragging logic.
 */
function handleMarkerDrag(markerHolder: TopLevelMarkerHolder) {
    if (markerHolder.halo) {
        updateMarkerVisibility(markerHolder.halo.inner, false); // Hide halo during drag
    }
    updateMarkerPosition(markerHolder.inner, getMarkerPosition(markerHolder.inner)); // Update marker position during drag
}

/**
 * Helper function to handle drag end logic.
 */
async function handleMarkerDragEnd(
    markerHolder: TopLevelMarkerHolder,
    markerDragEndCallback: MarkerCallbackFunction,
    point: GetPointResponse
) {
    const position = getMarkerPosition(markerHolder.inner);
    const positionString = `${position?.lat}, ${position?.lng}`;

    if (positionString) {
        const locationBeforeDrag: Location = point.location;
        point.location = { search: positionString, place_id: 'custom', latLng: position };
        setMarkerDraggable(markerHolder.inner, false);
        // this needs to match exactly with the logic in the onPointMarkerDragend function in map-component.ts
        await markerDragEndCallback(markerHolder, locationBeforeDrag);
    }

    if (markerHolder.halo) {
        updateMarkerPosition(markerHolder.halo.inner, position); // Update halo position after drag ends
    }
}

/**
 * Creates a styled background div for the marker based on marker type.
 */
function createMarkerBackgroundDiv(advancedMarkers: boolean, iconSize: number): HTMLDivElement {
    const backgroundDiv = document.createElement('div');
    // add class
    backgroundDiv.classList.add('marker-background');
    backgroundDiv.style.position = 'absolute';
    // backgroundDiv.style.top = advancedMarkers ? '0' : `calc(50% - ${iconSize / 2}px)`; [melinda removed when we moved to absolute positioning and not just above the latlng]
    backgroundDiv.style.left = '50%';
    backgroundDiv.style.width = advancedMarkers ? '120%' : `${iconSize}px`;
    backgroundDiv.style.height = advancedMarkers ? '120%' : `${iconSize}px`;
    backgroundDiv.style.backgroundColor = '#FFFFFF';
    backgroundDiv.style.border = '5px solid #FFFFFF';
    backgroundDiv.style.borderRadius = '50%';
    backgroundDiv.style.transform = 'translate(-50%, -50%) scale(1.2)';
    backgroundDiv.style.zIndex = '-1';
    backgroundDiv.style.boxShadow = 'rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px';
    return backgroundDiv;
}

/**
 * Creates a styled text div for the marker.
 */
function createMarkerTextDiv(name: string | undefined): HTMLDivElement {
    const textDiv = document.createElement('div');
    // add class
    textDiv.classList.add('marker-text');
    textDiv.style.position = 'absolute';
    textDiv.style.top = 'calc(50% + 20px)';
    textDiv.style.left = '50%';
    textDiv.style.width = 'auto';
    textDiv.style.transform = 'translateX(-50%)';
    textDiv.style.backgroundColor = '#FFFFFF';
    textDiv.style.padding = '5px 10px';
    textDiv.style.fontSize = '12px';
    textDiv.style.fontFamily = 'var(--custom-font), Montserrat, sans-serif';
    textDiv.style.textAlign = 'center';
    textDiv.style.borderRadius = '3px';
    textDiv.style.boxShadow = 'rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px';
    textDiv.textContent = name && name.length > 20 ? `${name.substring(0, 20)}...` : name ?? '';
    return textDiv;
}

function startCustomOverlay() {
    return class CustomMarkerOverlay extends google.maps.OverlayView {
        private div: HTMLDivElement | null = null;

        constructor(
            private readonly map: google.maps.Map,
            private readonly position: google.maps.LatLng,
            private readonly text: string,
            private readonly icon_size: number,
            private readonly type: 'background' | 'text'
        ) {
            super();
        }

        // This is implementing a base class member.
        // Called after `setMap(map)` is called.
        // We do this in `show()` to ensure the overlay is added to the map.
        // No need to hide anything here, since this done dynamically when shown.
        onAdd() {
            const div = document.createElement('div');
            div.style.position = 'absolute';

            if (this.type === 'background') {
                const backgroundDiv = createMarkerBackgroundDiv(false, this.icon_size);

                backgroundDiv.style.display = 'block';
                backgroundDiv.style.zIndex = '998';

                div.appendChild(backgroundDiv);
            }

            if (this.type === 'text') {
                const textDiv = createMarkerTextDiv(this.text);

                textDiv.style.display = 'block';
                textDiv.style.zIndex = '999';

                div.appendChild(textDiv);
            }

            this.div = div;

            const panes = this.getPanes();
            panes?.markerLayer.appendChild(div);
        }

        // This is implementing a base class member.
        // This is called automatically by the base class after `onAdd()`.
        // This should just position the div on the map.
        draw() {
            if (this.div) {
                const overlayProjection = this.getProjection();
                const position = overlayProjection.fromLatLngToDivPixel(this.position);

                if (position) {
                    this.div.style.left = `${position.x}px`;
                    this.div.style.top = `${position.y}px`;
                }
            }
        }

        // This is implementing a base class member.
        // This is called when the overlay is removed from the map (usually via `setMap(null)`).
        // So, let's just destroy our div.
        onRemove() {
            this.div?.parentNode?.removeChild(this.div);
            this.div = null;
        }

        // This is a "public" method to show the overlay on the map.
        show() {
            this.setMap(this.map);
        }

        // This is a "public" method to hide the overlay from the map.
        hide() {
            this.setMap(null);
        }
    };
}

// It seems `OverlayClass` doesn't like to be created and destroyed like it says it does,
// so we can create one, and destroy, as needed.
function createAndShowCustomOverlay(markerHolder: TopLevelMarkerHolder) {
    if (!markerHolder.map) {
        return;
    }

    // if there are already overlays, remove them
    if (isDefined(markerHolder.focusDivs)) {
        if (isDefined(markerHolder.focusDivs?.mBackgroundOverlay)) {
            markerHolder.focusDivs.mBackgroundOverlay?.hide();
            markerHolder.focusDivs.mBackgroundOverlay = null;
        }
        if (isDefined(markerHolder.focusDivs?.mTextBelowOverlay)) {
            markerHolder.focusDivs.mTextBelowOverlay?.hide();
            markerHolder.focusDivs.mTextBelowOverlay = null;
        }
    }
    // if the marker is a custom shape, don't show the overlay
    if (markerHolder.customShape) {
        return;
    }

    const category = topicData.topic?.categories.find(
        (c) => c._id?.$oid === markerHolder.point.topic_category_id?.$oid
    );
    const iconSize = category?.icon_size ?? topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE;
    const isHidden = markerHolder.point.is_gate_hidden && topicData.topic?.is_gate_limited === true;
    const position = new google.maps.LatLng(markerHolder.point.location.latLng.lat, markerHolder.point.location.latLng.lng);
    const OverlayClass = startCustomOverlay();
    const name = isHidden ? 'Hidden' : markerHolder.point.name ?? '';

    // Create the circle overlay (it does not get added to the map: it is dynamically handled).

    const circleOverlay = new OverlayClass(markerHolder.map, position, name, iconSize, 'background');

    // Create the text overlay if the point has a name (it does not get added to the map: it is dynamically handled).

    let textOverlay: any | undefined;
    if (markerHolder.point.name) {
        textOverlay = new OverlayClass(markerHolder.map, position, name, iconSize, 'text');
    }

    // [ARoney] Not sure what this is for?  Can we remove?
    // Looking around, then may not be necessary?

    markerHolder.focusDivs = {
        mBackgroundOverlay: circleOverlay,
        mTextBelowOverlay: textOverlay,
    };

    circleOverlay.show();
    textOverlay?.show();
}

// SVG helpers (used for setting special `Marker` icons).

// Create a cluster icon with a specific color and size.
// This has three concentric circles with decreasing opacity, giving a nice visual effect.
export function getClusterIcon(color: string, size: number): google.maps.Icon {
    // create svg url with fill color
    const svg = window.btoa(`
        <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
            <circle cx="120" cy="120" opacity=".6" r="70" />
            <circle cx="120" cy="120" opacity=".2" r="100" />
            <circle cx="120" cy="120" opacity=".1" r="120" />
        </svg>
    `);

    size = Math.max(size, 32);

    return {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(size / 2, size / 2),
    };
}

// Get the icon for a category cluster, using the top category's icon if available.
// This take X (up to 5) category icons and combines them into a single SVG image.
// It's the same as the above, except with the X icons equally space around the center.
// Get the icon for a category cluster, using category icons and showing the count in the center
export function getCategoryClusterIcon(backgroundColor: string, textColor: string, count: number, size: number, iconUrls: string[]) {
    if (iconUrls.length === 0) {
        return getClusterIcon(backgroundColor, size);
    }

    // Use a much larger viewBox size to ensure outer circles don't get cut off
    const viewBoxSize = 800;
    const adjusted_size = Math.max(size, 32);

    // Fixed inner element sizes to keep them consistent
    const iconPlacementRadius = 80; // Fixed distance from center for category icons
    const iconSize = 100; // Fixed icon size
    const centerCircleRadius = 75; // Fixed center circle size
    const fontSize = 60; // Fixed font size for count
    const strokeWidth = 5; // Fixed stroke width for icon borders

    // Scale only the outer circles, allowing them to grow much larger
    const primaryRadius = 100; // Fixed inner circle
    const secondaryRadius = 200; // Fixed middle circle
    const tertiaryRadius = 300; // Much larger outer circle, but still within viewBox

    const fontString = `Montserrat, Arial, sans-serif`;

    // Create an SVG with the icons arranged in a circle
    const svg = window.btoa(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewBoxSize} ${viewBoxSize}">
            <!-- Background circles - centered in the larger viewBox -->
            <circle cx="${viewBoxSize/2}" cy="${viewBoxSize/2}" opacity=".9" r="${primaryRadius}" fill="${backgroundColor}" />
            <circle cx="${viewBoxSize/2}" cy="${viewBoxSize/2}" opacity=".5" r="${secondaryRadius}" fill="${backgroundColor}" />
            <circle cx="${viewBoxSize/2}" cy="${viewBoxSize/2}" opacity=".2" r="${tertiaryRadius}" fill="${backgroundColor}" />

            <!-- Category icons around the perimeter - fixed size -->
            ${iconUrls.length > 1 ? `
                ${iconUrls.reverse().map((url, index) => {
                    const iconsCount = Math.min(iconUrls.length, 8);
                    const unreversedIndex = iconsCount - index - 1; // Reverse the order of icons
                    const angle = (unreversedIndex / iconsCount) * 2 * Math.PI - (Math.PI / 2);

                    // Calculate position on the circle - keep the same placement radius
                    const x = viewBoxSize/2 + iconPlacementRadius * Math.cos(angle);
                    const y = viewBoxSize/2 + iconPlacementRadius * Math.sin(angle);

                    // Calculate background circle size
                    const bgRadius = iconSize * 0.7;

                    return `
                        <!-- White circle background for icon -->
                        <circle cx="${x}" cy="${y}" r="${bgRadius}" fill="white" stroke="${backgroundColor}" stroke-width="${strokeWidth}" />

                        <!-- Icon image -->
                        <image href="${url}" x="${x - iconSize/2}" y="${y - iconSize/2}" width="${iconSize}" height="${iconSize}" />
                    `;
                }).join('')}` : ''}

            <!-- Center circle with count -->
            <circle cx="${viewBoxSize/2}" cy="${viewBoxSize/2}" r="${centerCircleRadius}" fill="${backgroundColor}" border="2px solid white" />
            <text x="${viewBoxSize/2}" y="${viewBoxSize/2 + fontSize/3}"
                  font-family="${fontString}"
                  font-size="${fontSize}"
                  text-anchor="middle"
                  fill="${textColor}"
                  font-weight="normal">${count}</text>
        </svg>
    `);

    return {
        url: `data:image/svg+xml;base64,${svg}`,
        scaledSize: new google.maps.Size(adjusted_size * 3, adjusted_size * 3), // Make the entire marker 3x larger
        anchor: new google.maps.Point(adjusted_size * 1.5, adjusted_size * 1.5), // Center properly with the new size
    };
}

export function getCategoryIcon(id: ObjectId | undefined): string {
    if (!id) {
        const random_color = DEFAULT_CATEGORY_COLORS[Math.floor(Math.random() * DEFAULT_CATEGORY_COLORS.length)];
        return makeColorIntoDataSvg(random_color, topicData.topic?.marker_shape ?? NumberedIconShape.Circle);
    }

    if (topicData.categoryIconDataUrls.size === 0) {
        const categoryIcon = topicData.categories.find(c => c._id?.$oid === id.$oid)?.icon;
        if (categoryIcon) {
            if (categoryIcon.startsWith('#')) {
                return makeColorIntoDataSvg(categoryIcon, getCategoryMarkerShape(topicData.topic, id.$oid));
            }
            // Data URLs are embedded and always valid, no need to validate
            if (categoryIcon.startsWith('data:')) {
                if (isGeneratedColorIcon(categoryIcon)) {
                    return makeColorIntoDataSvg(
                        getColorFromCircleIcon(categoryIcon) ?? palette.primaryBlue,
                        getCategoryMarkerShape(topicData.topic, id.$oid)
                    );
                }
                return categoryIcon;
            }
            // Validate external icon URLs before using, fallback to default if invalid
            return getValidatedIcon(categoryIcon);
        }
        return DEFAULT_CATEGORY_ICON;
    }

    const icon = topicData.categoryIconDataUrls.get(id.$oid);
    if (icon) {
        if (isGeneratedColorIcon(icon)) {
            return makeColorIntoDataSvg(
                getColorFromCircleIcon(icon) ?? palette.primaryBlue,
                getCategoryMarkerShape(topicData.topic, id.$oid)
            );
        }
        // Data URLs from the cache map are always valid
        return icon;
    }

    const random_color = DEFAULT_CATEGORY_COLORS[Math.floor(Math.random() * DEFAULT_CATEGORY_COLORS.length)];
    return makeColorIntoDataSvg(random_color, getCategoryMarkerShape(topicData.topic, id.$oid));
}